import {
  type AudioContext,
  type IAnalyserNode,
  type IAudioContext,
} from 'standardized-audio-context';

export type AudioStatus =
  | 'SILENT'
  | 'VERY_LOW'
  | 'LOW'
  | 'NORMAL'
  | 'TOO_HIGH'
  | 'UNKNOWN';

interface AudioLevelMonitorOptions {
  checkInterval: number;
  smoothing: number;
  peakWindow: number;
  onStatusChange: (status: AudioStatus) => void;
  onMicStatusChange: (status: AudioStatus) => void;
}

export class AudioLevelMonitor {
  private options: {
    checkInterval: number;
    smoothing: number;
    peakWindow: number;
  };

  private isRunning: boolean;
  private lastStatus: AudioStatus | null;
  private lastMicStatus: AudioStatus | null;
  private smoothedRMS: number;
  private micSmoothedRMS: number;
  private micAnalyzer: IAnalyserNode<AudioContext>;
  private tabAnalyzer?: IAnalyserNode<AudioContext>;
  private micBufferLength: number;
  private tabBufferLength: number;
  private micTimeData: Float32Array;
  private tabTimeData?: Float32Array;
  private checkTimer: number | null;
  private peakValues: Array<{ rms: number; timestamp: number }>;
  private currentPeakRMS: number;
  private micPeakRms: Float64Array;
  private micPeakTimes: Float64Array;
  private micPeakCursor: number;
  private micPeakCount: number;
  private onStatusChange?: (status: AudioStatus) => void;
  private onMicStatusChange?: (status: AudioStatus) => void;

  constructor(
    micAnalyzer: IAnalyserNode<IAudioContext>,
    tabAnalyzer?: IAnalyserNode<IAudioContext>,
    options: Partial<AudioLevelMonitorOptions> = {}
  ) {
    this.options = { checkInterval: 40, smoothing: 0.5, peakWindow: 1000, ...options };
    this.onStatusChange = options.onStatusChange;
    this.onMicStatusChange = options.onMicStatusChange;
    this.isRunning = false;
    this.lastStatus = null;
    this.lastMicStatus = null;
    this.smoothedRMS = 0;
    this.micSmoothedRMS = 0;
    this.checkTimer = null;
    this.micAnalyzer = micAnalyzer;
    this.tabAnalyzer = tabAnalyzer;
    this.micBufferLength = this.micAnalyzer.fftSize;
    this.tabBufferLength = this.tabAnalyzer?.fftSize ?? 0;
    this.micTimeData = new Float32Array(this.micBufferLength);
    this.tabTimeData = this.tabAnalyzer
      ? new Float32Array(this.tabBufferLength)
      : undefined;
    this.peakValues = [];
    // A fixed window avoids allocating and filtering an array on every 40 ms tick.
    const micPeakCapacity = Math.max(
      1,
      Math.ceil(this.options.peakWindow / Math.max(this.options.checkInterval, 1)) + 1
    );
    this.micPeakRms = new Float64Array(micPeakCapacity);
    this.micPeakTimes = new Float64Array(micPeakCapacity);
    this.micPeakCursor = 0;
    this.micPeakCount = 0;
    this.currentPeakRMS = 0;
  }

  private getAudioStatus(rms: number): AudioStatus {
    if (rms < 0.0001) return 'SILENT';
    if (rms < 0.01) return 'VERY_LOW';
    if (rms < 0.1) return 'LOW';
    if (rms < 0.9) return 'NORMAL';
    return 'TOO_HIGH';
  }

  private updatePeakTracking(rms: number): void {
    const now = Date.now();
    this.peakValues.push({ rms, timestamp: now });

    this.peakValues = this.peakValues.filter(
      (value) => now - value.timestamp < this.options.peakWindow
    );

    this.currentPeakRMS =
      this.peakValues.length > 0 ? Math.max(...this.peakValues.map((v) => v.rms)) : rms;
  }

  public start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.checkLevels();
  }

  public stop(): void {
    this.isRunning = false;

    if (this.checkTimer !== null) {
      window.clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }

    this.peakValues = [];
    this.micPeakCount = 0;
    this.micPeakCursor = 0;
    this.currentPeakRMS = 0;
  }

  private checkLevels(): void {
    if (!this.isRunning) return;
    this.micAnalyzer.getFloatTimeDomainData(this.micTimeData);

    let micSumOfSquares = 0;
    for (let i = 0; i < this.micBufferLength; i++) {
      const sample = this.micTimeData[i] ?? 0;
      micSumOfSquares += sample * sample;
    }
    const micRMS = Math.sqrt(micSumOfSquares / this.micBufferLength);
    this.micSmoothedRMS =
      this.options.smoothing * this.micSmoothedRMS +
      (1 - this.options.smoothing) * micRMS;
    const now = Date.now();
    this.micPeakRms[this.micPeakCursor] = this.micSmoothedRMS;
    this.micPeakTimes[this.micPeakCursor] = now;
    this.micPeakCursor = (this.micPeakCursor + 1) % this.micPeakRms.length;
    this.micPeakCount = Math.min(this.micPeakCount + 1, this.micPeakRms.length);
    let micPeakRMS = this.micSmoothedRMS;
    for (let i = 0; i < this.micPeakCount; i++) {
      if (now - this.micPeakTimes[i]! < this.options.peakWindow) {
        micPeakRMS = Math.max(micPeakRMS, this.micPeakRms[i]!);
      }
    }
    const micStatus = this.getAudioStatus(micPeakRMS);
    if (this.lastMicStatus !== micStatus) {
      this.lastMicStatus = micStatus;
      this.onMicStatusChange?.(micStatus);
    }

    let tabRMS = 0;
    if (this.tabAnalyzer && this.tabTimeData && this.tabBufferLength > 0) {
      this.tabAnalyzer.getFloatTimeDomainData(this.tabTimeData);

      let tabSumOfSquares = 0;
      for (let i = 0; i < this.tabBufferLength; i++) {
        const sample = this.tabTimeData[i] ?? 0;
        tabSumOfSquares += sample * sample;
      }

      tabRMS = Math.sqrt(tabSumOfSquares / this.tabBufferLength);
    }

    const instantRMS = Math.max(micRMS, tabRMS);

    this.smoothedRMS =
      this.options.smoothing * this.smoothedRMS +
      (1 - this.options.smoothing) * instantRMS;

    this.updatePeakTracking(this.smoothedRMS);
    const currentStatus = this.getAudioStatus(this.currentPeakRMS);

    if (this.lastStatus !== currentStatus) {
      this.lastStatus = currentStatus;
      this.onStatusChange?.(currentStatus);
    }

    this.checkTimer = window.setTimeout(
      () => this.checkLevels(),
      this.options.checkInterval
    );
  }
}
